/**
 * Constant settings which should never change, even when the environment has changed.
 * Used for general configuration values which may need to change in the future such as URL's
 */

import type { ChainsFromConfig } from './configType';
import type { PayoutProgram } from '@katanaperps/katana-perps-sdk';

/** Project name */
export const KATANA_PERPS = 'Katana Perps';

/**
 * EIP-6963 discovered wallets that should be hidden from the wallet list
 * because they are not supported by the app.
 */
export const HIDDEN_DISCOVERED_WALLET_RDNS: string[] = ['app.phantom'];

export const KATANA_DESC = 'Katana Perps';
// export const KATANA_TOKEN = 'KAT' as const;

// import type { Chains } from 'reduxState/exchange/exchangeActiveChainUtils';

export type PayoutProgramCode = PayoutProgram | 'competitionTestNet';

export type PayoutAsDepositProgramCode = Extract<
  PayoutProgramCode,
  'referralRewards' | 'syntheticFarms' | 'tradingCompetitions'
>;

export const APP_VERSION =
  __APP_VERSION__ ||
  // import.meta.env.VITE_RELEASE_VERSION ||
  // process.env.REACT_APP_RELEASE_VERSION ||
  'Unknown';

const MAINTENANCE_ENABLED = false;

const MAINTENANCE_BYPASSED =
  window?.document.location.search === '?DISABLE_MAINTENANCE';

/**
 * @note To enforce existing users to refresh page use `updateCheck.json`
 */
export const MAINTENANCE_MODE = MAINTENANCE_ENABLED && !MAINTENANCE_BYPASSED;

/** Show Welcome Modal overlay (blocks UI). On Sandbox is ignored. */
export const CODE_REQUIRED_OVERLAY = true;

export const MAINTENANCE_NOTICE = false;

export const TPSL_ENABLED = true;

export const REFERRALS_ENABLED = true;

export const REFERRAL_CODES_ENABLED = true;

// Enable one - Trade rewards or Points
export const TRADE_REWARDS_ENABLED = false;
export const POINTS_ENABLED = true;

export const COMPETITION_ENABLED = false;

export const MARKET_MAKING_REWARDS_ENABLED = false;

export const VAULTS_ENABLED = false;

export const PNL_SHARE_ENABLED = true;

/** Earn/staking functionality */
export const STAKING_ENABLED = false;

export const IGNORED_NONCE = 'ignored-nonce';

export const HIGH_SPREAD_MARKET_ORDER_PERCENTAGE_LIMIT = 30;

const docs = (path: string): `https://${string}` =>
  `https://docs-perps.katana.network${path}`;

/**
 * Constant URLs used throughout the UX to provide links to external or internal content
 */
export const urls = {
  exchange: 'https://perps.katana.network',

  socialDiscord: 'https://discord.gg/a3HFV3ZJPQ',
  socialX: 'https://x.com/katanaperps',
  socialXBase: 'https://x.com/',
  socialTelegram: 'https://t.me/Katana_Network/23424',
  socialBlog: 'https://katana-perps.beehiiv.com',

  katanaDocs: docs(''),
  // TODO: katana verify docs links work
  katanaDocsMMRQualify: docs('/rewards/market-maker-program'),
  katanaDocsClockSync: docs('/faq/faq#clock-sync'),
  // TODO: katana bgt emissions remove link
  kumaDocsTradeRewardsProgram: docs('#'),
  katanaDocsPointsRewardsProgram: docs('/rewards/points'),

  apiDocs: 'https://api-docs-v1-perps.katana.network/',
  apiDocsForRewards:
    'https://api-docs-v1-perps.katana.network/#rewards-endpoints',

  learnMoreQrSync: docs('/katana-perps-connect'),
  delegatedKeysDocs: docs('/session-keys'),

  // feedbackFormMobile: 'https://docs.google.com/forms/d/e/1FAIpQLSedI2RSuN4TT9MNHozY0-whR3wobaRhn5KseDydFLrh3-6VrA/viewform',
  // ledgerBuyLink: 'https://shop.ledger.com/products/ledger-nano-s?r=999d',

  replicatorStakingBlogPost: docs('/overview/idex-staking'),
  replicatorStakingDocs: docs('/user-guides/staking'),
  replicatorStakingRestartingGuide: docs(
    '/user-guides/staking/staking#stopping-and-restarting-your-node'
  ),
  replicatorStakingHostOwnNode: docs(
    '/user-guides/staking/staking/host-a-public-staking-node'
  ),
  replicatorStakingGithub: 'https://github.com/idexio/staking-replicator',
  referralsDetailsBlogPost: docs('/rewards/referrals'),
} as const;

/** Also ethers.ZeroAddress */
export const ETH_ADDRESS_ZERO = '0x0000000000000000000000000000000000000000';

export const MAX_ERC20_VALUE = 2n ** 256n - 1n;

export const EXCHANGE_DECIMALS = 8;

export const ZERO_API_NUMBER = '0.00000000';

/** Further reduced as <marketTickSize, SDK@getMaximumTickSizeUnderSpread> */
export const ORDERBOOK_MAX_TICK_SIZES: {
  [market: string]: number | undefined;
} = { 'BTC-USD': 100, 'ETH-USD': 100, 'SOL-USD': 10 };

/** Quite symbol and only available token for deposit */
// export const DEPOSIT_ASSET_SYMBOL = 'USDC';

/** Most chains use 6 decimals with some exceptions, see `usdcTokenDecimalsOverride` eg. BSC = 18 */
export const DEFAULT_USDC_DECIMALS = 6;
export const KATANA_USDC_DECIMALS = DEFAULT_USDC_DECIMALS;

/** Maximal number which our backend allow for Total and Amount fields */
export const MAX_ORDER_AMOUNT_OR_TOTAL = 100_000_000_000;

export const NATIVE_ASSET_DECIMALS = 18;

/**
 * Private key length is exactly 64 Bytes + 2 Bytes for "0x" = 66 characters
 * https://ethereum.stackexchange.com/questions/3542/how-are-ethereum-addresses-generated
 */
export const WALLET_PRIVATE_KEY_CHARS = 66;

/**
 * Public wallet address is address derived from public key and is exactly 40 Bytes long
 * + 2 Bytes for "0x" prefix
 * https://ethereum.stackexchange.com/questions/3542/how-are-ethereum-addresses-generated
 */
export const WALLET_PUBLIC_ADDRESS_CHARS = 42;

/** Our internal endpoints returns X pages maximum to prevent perf issues */
export const PAGINATION_PAGES_LIMIT = 10;

/**
 * API can be sometimes slow to propagate changes
 * We have added delays between websocket message and web client update
 * Also added random delay modification, to prevent running all web clients at once
 *
 * RELOAD_MARKETS_DELAY_MS & RELOAD_EXCHANGE_STATUS_CONTROLS_DELAY_MS:
 *  Both of these requests depend on a back end cache that is reloaded every 10 seconds.
 *  To ensure the response is fresh, the client should not delay less than 10 seconds.
 */

// 10 - 13 seconds
export const RELOAD_MARKETS_DELAY_MS = Math.random() * 1000 * 4 + 10_000;
export const RELOAD_MARKETS_TICKERS_DELAY_MS = RELOAD_MARKETS_DELAY_MS;

// 0 - 15 seconds
export const RELOAD_NOTICES_DELAY_MS = Math.random() * 1000 * 15;

// 10 - 13 seconds
export const RELOAD_EXCHANGE_STATUS_CONTROLS_DELAY_MS =
  Math.random() * 1000 * 3 + 10_000;

export const WITHDRAWAL_MINED_FETCH_DELAY_MS = 5000;
export const DEPOSIT_MINED_FETCH_DELAY_MS = 1000;
export const PAYOUT_MINED_FETCH_DELAY_MS = 2000;
// export const USER_UPDATE_DELAY_MS = 2000;

export const SAVE_STATE_MAX_DELAY_MS = 1000;

export const REWARDS_BRIDGE_SOURCE = 'katana.referralReward' as const;

export const BANKROLL_BRIDGE_SOURCE = 'katana.walletBankroll' as const;

/** Initially we make app invite only */
export const REFERRAL_ONLY_UI = true;

/** It represent all chains IDEX was running previously and is now. */
export type Chains = keyof typeof chainsMap;

// TODO: katana chains map - single chain now? Remove legacy and migration stuff.
export const chainsMap = {
  // Named intentionally so it's easy to search for in codebase
  katanaEVMChain: {
    code: 'katanaEVMChain',
    configProp: 'katanaChain',
    name: 'Katana',
    title: 'Katana',
    shortCode: 'katana',
    fallbackDefaultMarket: 'BTC-USD',
  },
  beraChain: {
    code: 'beraChain',
    configProp: 'beraChain',
    name: 'Berachain',
    title: 'Berachain',
    shortCode: 'bera',
    fallbackDefaultMarket: 'BTC-USD',
  },
  polygonPoSChain: {
    code: 'polygonPoSChain',
    configProp: 'polygonPoS',
    name: 'Polygon PoS Chain',
    title: 'PolygonPoS',
    shortCode: 'polygonpos',
    fallbackDefaultMarket: 'BTC-USD',
  },
  ethereumChain: {
    code: 'ethereumChain',
    configProp: 'ethereum',
    name: 'Ethereum Chain',
    title: 'Ethereum',
    shortCode: 'eth',
    fallbackDefaultMarket: 'IDEX-ETH',
  },
  binanceChain: {
    code: 'binanceChain',
    configProp: 'bnbSmartChain',
    name: 'Binance Smart Chain',
    title: 'BSC',
    shortCode: 'bsc',
    fallbackDefaultMarket: 'ETH-BNB',
  },
} as const satisfies {
  [chainCode: string]: {
    code: string;
    configProp: ChainsFromConfig;
    name: string;
    title: string;
    shortCode: string;
    fallbackDefaultMarket: string;
  };
};

export const chainsList = [
  chainsMap.katanaEVMChain,
  // chainsMap.xchainEVMChain,
  // chainsMap.ethereumChain,
  // chainsMap.binanceChain,
] as const;

/** Networks which are not covered by environments.ts */
export const NETWORKS_BY_ID_OVERRIDE: {
  [chainId: number]: string;
} = {
  11155111: 'Ethereum Sepolia Testnet',
  // TODO: add all known Mainnet chains we might need - for walletConnect multichain support
};

/** Highlighted chain as "new". Note: Deposit/Withdraw must be enabled */
export const ALWAYS_FIRST_CHAIN = 'katanaChain' as const;
export const HIGHLIGHTED_NEW_CHAIN = undefined; // 'katanaChain' as const;
export const HIGHLIGHTED_CHEAPEST_CHAIN = undefined; // 'katanaChain' as const;

/** Estimate of Bridged (stargate) Deposit time to fully process deposit in minutes */
export const chainsDepositTimeMinMap: {
  [chain in ChainsFromConfig]: number;
} = {
  arbitrum: 5,
  avalanche: 5,
  baseEVM: 5,
  bnbSmartChain: 5,
  ethereum: 5,
  mantle: 5,
  optimism: 5,
  polygonPoS: 15,
  seiChain: 5,
  beraChain: 5,
  // TODO: katana verify deposit time
  katanaChain: 1,
};

/** Cross chain bridge options */
const _bridgesList = ['Stargate'] as const;

export type CrossChainBridge = (typeof _bridgesList)[number];

export const API_ERROR_MARKET_NOT_FOUND = 'MARKET_NOT_FOUND';
